import baseApi from '@store/api/baseApi';
import { Types } from '@store/types';

import { capitalizeFirstLetter } from '@utils/Util';

export const handleMaterialRequisitionQuery = async (queryFulfilled, getState, dispatch) => {
	try {
		let materialRequisition = getState().materialRequisitionStore.materialRequisition;
		const { data } = await queryFulfilled;
		let tmpMaterials = [];
		if (data?.data?.length) {
			const combined = data.currentPage > 0 ? [...materialRequisition.data, ...data.data] : data.data;
			tmpMaterials = Array.from(new Map(combined.map((item) => [item.id, item])).values());
		} else if (data?.currentPage === 0) {
			tmpMaterials = [];
		}
		let modifiedData = {
			currentPage: data.currentPage,
			totalElements: data.totalElements,
			totalPages: data.totalPages,
			data: tmpMaterials
		};
		dispatch({
			type: Types.SET_MATERIAL_REQUISITION,
			payload: modifiedData
		});
	} catch {}
};

export const updateMaterialRequisitionQuery = async (queryFulfilled, getState, dispatch) => {
	try {
		let materialRequisition = getState().materialRequisitionStore.materialRequisition;
		const { data } = await queryFulfilled;
		let modifiedList = materialRequisition.data.map((item) => {
			if (item.id === data.id) {
				return { ...data };
			}
			return item;
		});
		let modifiedData = {
			...materialRequisition,
			data: modifiedList
		};
		await dispatch({
			type: Types.SET_MATERIAL_REQUISITION,
			payload: modifiedData
		});
	} catch {}
};

export const updateMaterialRequisitionPreliminaryItemsQuery = async (queryFulfilled, getState, dispatch) => {
	try {
		let materialRequisition = getState().materialRequisitionStore.materialRequisition;
		const { data } = await queryFulfilled;
		let modifiedList = materialRequisition.data.map((item) => {
			if (item.id === data?.materialRequisitionResponse?.id) {
				return { ...data.materialRequisitionResponse };
			}
			return item;
		});
		let modifiedData = {
			...materialRequisition,
			data: modifiedList
		};
		await dispatch({
			type: Types.SET_MATERIAL_REQUISITION,
			payload: modifiedData
		});
	} catch {}
};

export const materialRequisitionApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchMaterialRequisitionSummary: builder.query({
			query: ({ requisitionType, requestBody = {} }) => ({
				url: `/material-requisition/summary?requisitionType=${requisitionType}`,
				method: 'POST',
				body: requestBody
			}),
			providesTags: ['MATERIAL_REQUISITION_SUMMARY']
		}),
		fetchMaterialRequisitions: builder.query({
			query: ({ params, requestBody = {} }) => ({
				url: `/material-requisition/all${params}`,
				method: 'POST',
				body: requestBody
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
				await handleMaterialRequisitionQuery(queryFulfilled, getState, dispatch);
			},
			providesTags: ['MATERIAL_REQUISITIONS']
		}),
		fetchMaterialRequisitionFilterOptions: builder.mutation({
			query: ({ requestBody = {} }) => ({
				url: `/material-requisition/filter-options`,
				method: 'POST',
				body: requestBody
			})
		}),
		addMaterialRequisition: builder.mutation({
			query: ({ requestBody = {} }) => ({
				url: `/material-requisition/add`,
				method: 'POST',
				body: requestBody
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
				try {
					let materialRequisition = getState().materialRequisitionStore.materialRequisition;
					const { data } = await queryFulfilled;
					let modifiedData = {
						currentPage: materialRequisition.currentPage,
						totalElements: (materialRequisition.totalElements || 0) + 1,
						totalPages: materialRequisition.totalPages,
						data: [data.payload, ...materialRequisition.data]
					};
					await dispatch({
						type: Types.SET_MATERIAL_REQUISITION,
						payload: modifiedData
					});
				} catch {}
			}
		}),
		fetchMaterialPreliminaryItems: builder.query({
			query: ({ requisitionId }) => ({
				url: `/material-requisition/preliminary-items/${requisitionId}?sort=id,desc`,
				method: 'GET'
			}),
			providesTags: ['MATERIAL_REQUISITION_PRELIMINARY_ITEMS']
		}),
		addMaterialRequisitionPreliminaryItems: builder.mutation({
			query: ({ requestBody = {} }) => ({
				url: `/material-requisition/preliminary-items/add`,
				method: 'POST',
				body: requestBody
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
				await updateMaterialRequisitionPreliminaryItemsQuery(queryFulfilled, getState, dispatch);
			},
			invalidatesTags: ['MATERIAL_REQUISITION_PRELIMINARY_ITEMS']
		}),
		updateMaterialRequisitionPreliminaryItem: builder.mutation({
			query: ({ requestBody = {} }) => ({
				url: `/material-requisition/preliminary-items/update`,
				method: 'PUT',
				body: requestBody
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
				await updateMaterialRequisitionPreliminaryItemsQuery(queryFulfilled, getState, dispatch);
			},
			invalidatesTags: ['MATERIAL_REQUISITION_PRELIMINARY_ITEMS']
		}),
		rejectMaterialRequisition: builder.mutation({
			query: ({ requisitionId, requestBody = {} }) => ({
				url: `/material-requisition/reject/${requisitionId}`,
				method: 'PUT',
				body: requestBody
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
				await updateMaterialRequisitionQuery(queryFulfilled, getState, dispatch);
			}
		}),
		removePreliminaryItemsById: builder.mutation({
			query: (id) => ({
				url: `/material-requisition/preliminary-items/${id}`,
				method: 'DELETE'
			}),
			invalidatesTags: ['MATERIAL_REQUISITION_PRELIMINARY_ITEMS']
		}),
		fetchCollectionList: builder.query({
			query: ({ search = '' }) => ({
				url: `/collection/search${search ? '?name=' + search : ''}`,
				method: 'GET'
			}),
			transformResponse(baseQueryReturnValue) {
				if (baseQueryReturnValue?.data) {
					return baseQueryReturnValue.data.map((item) => ({
						label: item.name,
						value: item.id,
						brandId: item.brandId,
						brandName: item.brand
					}));
				}
				return [];
			}
		}),
		fetchMyFolderList: builder.query({
			query: ({ search = '' }) => ({
				url: `/user/favourites/my-folders${search ? '?search=' + search : ''}`,
				method: 'GET'
			}),
			transformResponse(baseQueryReturnValue) {
				if (baseQueryReturnValue) {
					return baseQueryReturnValue.map((item) => ({
						label: item.name,
						value: item.id,
						brandId: item?.brandResponse?.id,
						brandName: item?.brandResponse?.name
					}));
				}
				return [];
			}
		}),
		fetchFabricTypeList: builder.query({
			query: () => ({
				url: `/material/fabric/construction-groups`,
				method: 'GET'
			}),
			transformResponse(baseQueryReturnValue) {
				if (baseQueryReturnValue) {
					return baseQueryReturnValue.map((item) => ({
						label: `${capitalizeFirstLetter(`${item?.fabricType} - ${item.name}`)}`,
						value: item.id
					}));
				}
				return [];
			}
		}),
		fetchMaterialDetailList: builder.query({
			query: ({ search = '', requisitionType }) => ({
				url: `/material/all/material-brief?page=0&size=20&supplierType=${requisitionType}${search ? '&search=' + search : ''}`,
				method: 'GET'
			}),
			transformResponse(baseQueryReturnValue) {
				if (baseQueryReturnValue?.data) {
					return baseQueryReturnValue.data.map((item) => ({
						...item,
						label: `${item.referenceNumber} : ${item.name}`,
						value: item.id,
						referenceNumber: item.referenceNumber
					}));
				}
				return [];
			}
		}),
		fetchTrimsOrAccessoriesDetailsById: builder.query({
			query: ({ materialId, materialType }) => ({
				url: `/material/${materialType === 'ACCESSORIES' ? 'branding' : 'trims'}/${materialId}`,
				method: 'GET'
			})
		}),
		fetchFabricDetailsById: builder.query({
			query: ({ materialId }) => ({
				url: `/material/fabric/fabric-with-supplier/${materialId}`,
				method: 'GET'
			})
		}),
		fetchTrimsAndAccessories: builder.query({
			query: ({ materialType }) => ({
				url: `/enum/list/info.nitex.app.materials.${materialType === 'ACCESSORIES' ? 'BrandingCategory' : 'TrimsCategory'}`,
				method: 'GET'
			}),
			transformResponse(baseQueryReturnValue) {
				if (baseQueryReturnValue) {
					return baseQueryReturnValue.map((item) => ({
						id: item.constant,
						value: item.name
					}));
				}
				return [];
			}
		}),
		fetchBrandList: builder.query({
			query: ({ name = '' }) => ({
				url: `/brand/allWithLogo${name ? '?name=' + name : ''}`,
				method: 'GET'
			}),
			transformResponse(baseQueryReturnValue) {
				if (baseQueryReturnValue?.data) {
					return baseQueryReturnValue.data.map((item) => ({
						label: item.name,
						value: item.id
					}));
				}
				return [];
			}
		}),
		fetchCompositionList: builder.query({
			query: () => ({
				url: `/material/fabric/fiber-list`,
				method: 'GET'
			}),
			transformResponse(baseQueryReturnValue) {
				if (baseQueryReturnValue) {
					return baseQueryReturnValue.map((item) => ({
						id: item.id,
						value: item.name
					}));
				}
				return [];
			}
		}),
		fetchMaterialRequisitionPurpose: builder.query({
			query: () => ({
				url: `/enum/list/info.nitex.app.materials.MaterialRequisitionPurpose`,
				method: 'GET'
			})
		}),
		fetchApprovalMaxToken: builder.query({
			query: () => ({
				url: `/material-requisition/approvalmax/token-exists`,
				method: 'GET'
			})
		}),
		generateApprovalMaxToken: builder.mutation({
			query: (code) => ({
				url: `/material-requisition/approvalmax/generate-token?code=${code}`,
				method: 'POST',
				body: {}
			})
		}),
		createApprovalMaxRequest: builder.mutation({
			query: (requisitionId) => ({
				url: `/material-requisition/approvalmax/create-request?requisitionId=${requisitionId}`,
				method: 'POST',
				body: {}
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
				await updateMaterialRequisitionQuery(queryFulfilled, getState, dispatch);
			}
		}),
		fetchMaterialRequisitionInventory: builder.query({
			query: ({ materialRequisitionId }) => ({
				url: `/material-requisition/inventory?materialRequisitionId=${materialRequisitionId}`,
				method: 'GET'
			})
		}),
		completeMaterialRequisitionById: builder.mutation({
			query: ({ requisitionId }) => ({
				url: `/material-requisition/complete/${requisitionId}`,
				method: 'PUT',
				body: {}
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
				await updateMaterialRequisitionQuery(queryFulfilled, getState, dispatch);
			}
		}),
		markAsFreeByRequisitionId: builder.mutation({
			query: ({ requisitionId }) => ({
				url: `/material-requisition/free-of-cost/${requisitionId}`,
				method: 'PUT',
				body: {}
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
				await updateMaterialRequisitionQuery(queryFulfilled, getState, dispatch);
			}
		}),
		forceApproveByRequisitionById: builder.mutation({
			query: ({ requisitionId }) => ({
				url: `/material-requisition/force-approve/${requisitionId}`,
				method: 'PUT',
				body: {}
			}),
			onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
				await updateMaterialRequisitionQuery(queryFulfilled, getState, dispatch);
			}
		})
	})
});

export const {
	useAddMaterialRequisitionMutation,
	useAddMaterialRequisitionPreliminaryItemsMutation,
	useUpdateMaterialRequisitionPreliminaryItemMutation,
	useLazyFetchMaterialRequisitionsQuery,
	useLazyFetchMaterialRequisitionSummaryQuery,
	useLazyFetchCollectionListQuery,
	useLazyFetchMyFolderListQuery,
	useFetchFabricTypeListQuery,
	useLazyFetchBrandListQuery,
	useFetchTrimsAndAccessoriesQuery,
	useFetchCompositionListQuery,
	useFetchMaterialRequisitionPurposeQuery,
	useLazyFetchApprovalMaxTokenQuery,
	useGenerateApprovalMaxTokenMutation,
	useCreateApprovalMaxRequestMutation,
	useFetchMaterialPreliminaryItemsQuery,
	useRemovePreliminaryItemsByIdMutation,
	useFetchMaterialRequisitionFilterOptionsMutation,
	useRejectMaterialRequisitionMutation,
	useLazyFetchMaterialDetailListQuery,
	useLazyFetchFabricDetailsByIdQuery,
	useLazyFetchTrimsOrAccessoriesDetailsByIdQuery,
	useFetchMaterialRequisitionInventoryQuery,
	useCompleteMaterialRequisitionByIdMutation,
	useMarkAsFreeByRequisitionIdMutation,
	useForceApproveByRequisitionByIdMutation
} = materialRequisitionApi;
